/**
 * Product Motive Picker
 *
 * The "other designs" swatch row on the PDP. The row is clipped to one line, and
 * a "+N" CTA overlays its right edge — the tile underneath peeks out from behind
 * it — where N is the number of designs the shopper cannot currently see.
 *
 * How many tiles fit is derived from the tile pitch rather than from each tile's
 * measured position, so the answer does not depend on how many tiles are hidden
 * at the time it runs. That makes it safe to re-run on every resize.
 *
 * Expected markup (see snippets/product-motive-picker.liquid):
 *
<motive-picker class="product-motive-list">
  <div class="product-motive-list__item js-product-motive-list__item">…</div>
  …
  <button class="product-motive__button-more js-drawer-cta js-motive-more"
          for="drawer-product-variants" data-total="49" hidden>
    <span class="js-motive-more-count">+49</span>
  </button>
</motive-picker>
 *
 */
class MotivePicker extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.js-product-motive-list__item'));
    this.cta = this.querySelector('.js-motive-more');
    this.output = this.cta && this.cta.querySelector('.js-motive-more-count');

    // Nothing to truncate, or the drawer was not rendered — leave the row alone.
    if (this.items.length < 2 || !this.cta || !this.output) return;

    this.total = parseInt(this.cta.dataset.total, 10) || 0;
    this.update = this.update.bind(this);

    this.update();

    // Fires after the first layout too, so this also covers the case where the
    // swatch images had no intrinsic size yet on the initial pass.
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /**
   * Picks the tile the CTA lands on and labels the CTA with everything that
   * does not clear it.
   */
  update() {
    const styles = getComputedStyle(this);
    const gap = parseFloat(styles.columnGap) || 0;
    const tile = this.items[0].getBoundingClientRect().width; // item 0 is never hidden
    if (!tile) return;

    const inner = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);

    // The CTA is pinned to the right edge and is one tile wide, so the tiles that
    // clear it are those fitting in the space to its left.
    const clearTiles = Math.max(1, Math.min(this.items.length, Math.floor((inner - 2 * tile) / (tile + gap)) + 1));
    const hidden = this.total - (clearTiles - 1); // tile 0 is the current product

    // The boundary tile stays in flow and peeks out from under the CTA.
    const boundary = Math.min(clearTiles, this.items.length - 1);
    this.items.forEach((item, index) => item.classList.toggle('last-visible', index === boundary));

    this.cta.hidden = hidden <= 0;
    if (!this.cta.hidden) this.output.textContent = `+${hidden}`;
  }
}

customElements.define('motive-picker', MotivePicker);
